import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  FluentProvider,
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Spinner,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { useActiveTheme } from '../theme/ThemeContext';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { formatDate, type IField, type IMetadata } from '@moko/utils';
import { Features, useFeatureFlag } from '@moko/utils/src/featureAccess';
import {
  BASIC_ORDER_FIELDS,
  PRICING_ORDER_FIELDS,
  orderFieldLabel,
  pickOrderValue,
  type OrderDetailFieldKey,
} from './orderDetailFields';
import { useTenantWorkTypes } from './useTenantWorkTypes';
import { workTypeReadLabel } from './WorkTypeField';

export type GetOrderService = (
  abortController: AbortController,
  orderId: number
) => Promise<any>;

export type GetOrderMetadataService = (
  abortController: AbortController,
  entityType?: string,
  allFields?: boolean,
  name?: string
) => Promise<IMetadata>;

interface OrderDetailsViewProps {
  orderId: number;
  getOrder: GetOrderService;
  getMetadata?: GetOrderMetadataService;
  getTenantWorkTypes?: (
    abortController: AbortController,
    tenantId?: number
  ) => Promise<{ id?: number; name: string }[]>;
  workTypeId?: number | null;
}

interface OrderDetailsDrawerProps extends OrderDetailsViewProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles({
  section: {
    marginBottom: tokens.spacingVerticalXXL,
  },
  // Section title (e.g. "Basic info"): slightly larger than labels/values, in
  // the softer neutral foreground, with an underline separating it from the
  // content.
  sectionTitle: {
    display: 'block',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    paddingBottom: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  sectionBody: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
  },
  sectionBodyStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
  },
  fullRow: {
    gridColumn: '1 / -1',
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  // Label: bolder than the value, in the strongest neutral foreground (the
  // section title uses the softer color).
  label: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  value: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    wordBreak: 'break-word',
  },
  center: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
});

const LabelValue = ({
  label,
  value,
  fullWidth,
}: {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}) => {
  const styles = useStyles();
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className={mergeClasses(styles.row, fullWidth && styles.fullRow)}>
      <Text className={styles.label}>{label}</Text>
      <Text className={styles.value}>{value}</Text>
    </div>
  );
};

const displayValue = (value: string | undefined, fallback: string) =>
  value == null || value === '' ? fallback : value;

const shortMetadataName = (value: unknown): string | undefined => {
  if (value == null || value === '') return undefined;
  const text = String(value);
  const sep = text.indexOf('|');
  return sep >= 0 ? text.slice(sep + 1) : text;
};

const toObject = (otherInfo: unknown): Record<string, unknown> | null => {
  if (!otherInfo) return null;
  let obj: unknown = otherInfo;
  if (typeof otherInfo === 'string') {
    try {
      obj = JSON.parse(otherInfo);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  return obj as Record<string, unknown>;
};

const stringifyValue = (value: unknown): string =>
  typeof value === 'object' ? JSON.stringify(value) : String(value);

// Builds the "Company specific info" rows from the order's `otherInfo`. When
// metadata fields are available we follow the form's field order and use each
// field's label (and resolve lookup keys to their display value); otherwise we
// fall back to the raw JSON keys.
const buildOtherInfoRows = (
  otherInfo: unknown,
  fields: IField[],
  lookupsByName: Map<string, Map<string, string>>
): { label: string; value: string }[] => {
  const obj = toObject(otherInfo);
  if (!obj) return [];

  if (fields.length > 0) {
    return fields
      .filter((f) => {
        const v = obj[f.name];
        return v !== null && v !== undefined && v !== '';
      })
      .map((f) => {
        const raw = obj[f.name];
        if (f.fieldType === 'Date' || f.fieldType === 'DateTime') {
          return {
            label: f.label || f.name,
            value: formatDate(
              String(raw),
              f.fieldType === 'DateTime' ||
                (!!f.options &&
                  typeof f.options === 'object' &&
                  !Array.isArray(f.options) &&
                  (f.options as { supportTimeInDateField?: unknown })
                    .supportTimeInDateField === true)
            ),
          };
        }
        const lookup = f.lookupList
          ? lookupsByName.get(f.lookupList)
          : undefined;
        const value = lookup?.get(stringifyValue(raw)) ?? stringifyValue(raw);
        return { label: f.label || f.name, value };
      });
  }

  return Object.entries(obj)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => ({ label: k, value: stringifyValue(v) }));
};

// Maps a base Order Details field to the display value on the loaded order
// payload. The payload already carries resolved display strings (status name,
// tenant name), so no lookup tables are needed here.
const orderFieldValue = (
  key: OrderDetailFieldKey,
  order: Record<string, unknown>
): string | undefined => {
  const v = ((): unknown => {
    switch (key) {
      case 'tenant':
        return order.tenantName;
      case 'code':
        return order.code;
      case 'description':
        return order.description;
      case 'status':
        return order.status;
      case 'orderForm':
        return shortMetadataName(order.metadataName);
      case 'orderEntity':
        return order.orderedEntityType;
      case 'orderedForm':
        return shortMetadataName(order.orderedMetadataName);
      case 'estimatedPrice':
        return pickOrderValue(order, 'estimatedPrice', 'EstimatedPrice');
      case 'actualPrice':
        return pickOrderValue(order, 'actualPrice', 'ActualPrice');
      case 'currencyCode':
        return pickOrderValue(order, 'currencyCode', 'CurrencyCode');
      case 'pricingItems': {
        const items = order.pricingItems;
        if (!Array.isArray(items)) return undefined;
        const ids = items
          .map((item) =>
            Number((item as { pricingItemId?: unknown }).pricingItemId)
          )
          .filter((id) => Number.isFinite(id));
        return ids.length > 0 ? ids.join(', ') : undefined;
      }
      default:
        return undefined;
    }
  })();
  if (v === null || v === undefined || v === '') return undefined;
  return String(v);
};

export const OrderDetailsView = ({
  orderId,
  getOrder,
  getMetadata,
  getTenantWorkTypes,
  workTypeId: workTypeIdFallback,
}: OrderDetailsViewProps) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const [order, setOrder] = useState<any | null>(null);
  const [metadata, setMetadata] = useState<IMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    setOrder(null);
    setMetadata(null);
    setFailed(false);
    setLoading(true);
    getOrder(ac, orderId)
      .then(async (res) => {
        setOrder(res);
        const fullName = res?.metadataName as string | undefined;
        if (getMetadata && fullName) {
          const name = fullName.includes('|')
            ? fullName.split('|')[1]
            : fullName;
          try {
            const md = await getMetadata(ac, 'Order', undefined, name);
            if (!ac.signal.aborted) setMetadata(md);
          } catch {}
        }
      })
      .catch(() => {
        if (ac.signal.aborted) return;
        setFailed(true);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [orderId, getOrder, getMetadata]);

  const lookupsByName = useMemo(() => {
    const map = new Map<string, Map<string, string>>();
    (metadata?.lookups ?? []).forEach((lookup) => {
      map.set(
        lookup.name,
        new Map(lookup.values.map((v) => [String(v.key), v.value]))
      );
    });
    return map;
  }, [metadata]);

  const companyInfo = order
    ? buildOtherInfoRows(order.otherInfo, metadata?.fields ?? [], lookupsByName)
    : [];

  const { isTenantReady, isEnabled: isOrderAssetEnabled } = useFeatureFlag(
    Features.FEATURE_ORDER_ASSET
  );
  const hideOrderEntity = isTenantReady && !isOrderAssetEnabled;
  const formIsWork =
    !!order && (hideOrderEntity || order.orderedEntityType === 'Work');
  const savedWorkTypeId =
    typeof order?.workTypeId === 'number'
      ? order.workTypeId
      : typeof workTypeIdFallback === 'number'
      ? workTypeIdFallback
      : null;
  const orderTenantId =
    typeof order?.tenantId === 'number' ? order.tenantId : undefined;
  const workTypes = useTenantWorkTypes(
    orderTenantId,
    formIsWork && typeof getTenantWorkTypes === 'function',
    getTenantWorkTypes
  );
  const workTypeLabel = workTypeReadLabel(
    savedWorkTypeId,
    workTypes.options,
    t
  );

  if (loading) {
    return (
      <div className={styles.center}>
        <Spinner size="small" label={t('loading', 'Loading...')} />
      </div>
    );
  }
  if (failed) {
    return (
      <div className={styles.center}>
        <Text className={styles.error}>
          {t('failedToLoad', 'Failed to load')}
        </Text>
      </div>
    );
  }
  if (!order) return null;

  const na = t('na', 'N/A');

  return (
    <>
      <div className={styles.section}>
        <Text className={styles.sectionTitle}>
          {t('basicInfo', 'Basic info')}
        </Text>
        <div className={styles.sectionBody}>
          {BASIC_ORDER_FIELDS.filter((field) => {
            if (field.hiddenInEdit) return false;
            if (field.hiddenWhenForceWork && hideOrderEntity) return false;
            if (
              field.key === 'workType' &&
              (!formIsWork || typeof getTenantWorkTypes !== 'function')
            ) {
              return false;
            }
            return true;
          }).map((field) => {
            const label = orderFieldLabel(field, formIsWork);
            const value =
              field.key === 'workType' ? (
                workTypes.loading ? (
                  <Spinner size="tiny" />
                ) : (
                  workTypeLabel
                )
              ) : (
                orderFieldValue(field.key, order)
              );
            return (
              <LabelValue
                key={field.key}
                label={t(label.key, label.fallback)}
                value={value}
                fullWidth={field.fullWidth}
              />
            );
          })}
        </div>
      </div>
      <div className={styles.section}>
        <Text className={styles.sectionTitle}>{t('pricing', 'Pricing')}</Text>
        <div className={styles.sectionBody}>
          {PRICING_ORDER_FIELDS.map((field) => {
            const label = orderFieldLabel(field, formIsWork);
            return (
              <LabelValue
                key={field.key}
                label={t(label.key, label.fallback)}
                value={displayValue(orderFieldValue(field.key, order), na)}
                fullWidth={field.fullWidth}
              />
            );
          })}
        </div>
      </div>
      {companyInfo.length > 0 && (
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>
            {t('companySpecificInfo', 'Company specific info')}
          </Text>
          <div className={styles.sectionBodyStack}>
            {companyInfo.map((row) => (
              <LabelValue key={row.label} label={row.label} value={row.value} />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

const OrderDetailsDrawer = ({
  open,
  onClose,
  orderId,
  getOrder,
  getMetadata,
  getTenantWorkTypes,
  workTypeId,
}: OrderDetailsDrawerProps) => {
  const theme = useActiveTheme();
  const { t } = useTranslation();

  return (
    <FluentProvider theme={theme}>
      <OverlayDrawer
        position="end"
        open={open}
        onOpenChange={(_, data) => {
          if (!data.open) onClose();
        }}
        size="medium"
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label={t('close', 'Close')}
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            }
          >
            {`${t('order', 'Order')} #${orderId}`}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          {open && (
            <OrderDetailsView
              orderId={orderId}
              getOrder={getOrder}
              getMetadata={getMetadata}
              getTenantWorkTypes={getTenantWorkTypes}
              workTypeId={workTypeId}
            />
          )}
        </DrawerBody>
      </OverlayDrawer>
    </FluentProvider>
  );
};

export default OrderDetailsDrawer;
